import classNames from 'classnames'
import { get } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Component, createRef } from 'react'
import Lightbox from 'react-image-lightbox'
import ReactPlayer from 'react-player/lazy'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Icon, Image } from 'semantic-ui-react'

import APP from 'app'
import { trackEvent } from 'global-utils/Analytics'
import { Avatar, Build, Shield } from 'images'
import { Markdown } from 'ui'

import PostSchema from '../../../schemas/PostSchema'
import SharedPostSchema from '../../../schemas/SharedPostSchema'
import UserSchema from '../../../schemas/UserSchema'
import Message from '../../modals/Message'
import { postListDelete } from '../PostListActions'
import PostOptions from './PostOptions'
import PostReactions from './PostReactions'

import './Post.css'

class Post extends Component {
  constructor(props) {
    super(props)
    this.state = {
      action: null,
      deleting: false,
      expandText: false,
      height: 0,
      isVideoOverflowed: false,
      messageEl: null,
      modal: false,
      width: 0,
    }
    this.videoRef = createRef()
  }

  static getDerivedStateFromProps(props, state) {
    const {
      post: { post: content },
    } = props
    const post = content || props.post

    let newState = {}
    if (props.post.id !== state.postId) {
      newState = {
        action: null,
        deleting: false,
        expandedTextHeight: 0,
        expandText: !post.picture,
        height: 0,
        isVideoOverflowed: false,
        modal: false,
        postId: props.post.id,
        width: 0,
      }
    }
    if (state.messageEl && state.messageEl.clientHeight) {
      const expandedTextHeight = Math.max(state.expandedTextHeight, state.messageEl.clientHeight)

      // @TODO Se debe refactorizar la funcionalidad de expandir el contenido ("ver mas") con SB-762
      let expandText = !post.picture || state.expandText || expandedTextHeight < 80
      if (expandedTextHeight > state.expandedTextHeight && expandedTextHeight > 80 && expandedTextHeight < 100) {
        expandText = false
      }

      let height = state.messageEl.clientWidth * (9 / 16)
      if (state.isVideoOverflowed) {
        height = state.height
      }

      Object.assign(newState, {
        expandedTextHeight,
        expandText,
        height,
        width: state.messageEl.clientWidth,
      })
    }
    return newState
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      ['smallDevice', 'tinyDevice', 'wall', 'isAdmin', 'post.id', 'post.post.id', 'post.post.reactions.length'].some(
        (key) => get(nextProps, key) !== get(this.props, key),
      ) ||
      Object.keys(nextState).some((key) => get(nextState, key) !== get(this.state, key)) ||
      get(nextProps, 'post.post.reactions', [])
        .map((r) => r.reaction)
        .toString() !==
        get(this.props, 'post.post.reactions', [])
          .map((r) => r.reaction)
          .toString() ||
      get(nextProps, 'post.post.reactions_summary', [])
        .map((r) => r.count)
        .toString() !==
        get(this.props, 'post.post.reactions_summary', [])
          .map((r) => r.count)
          .toString()
    )
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.postId !== prevState.postId) {
      this.setState({
        expandedTextHeight: 0,
        expandText: false,
        width: 0,
      })
    }
  }

  componentWillUnmount() {
    this.onImageModalClose()
  }

  checkVideoOverflow = () => {
    let isVideoOverflowed = false
    let { height } = this.state

    if (this.videoRef.current) {
      isVideoOverflowed = this.videoRef.current.wrapper.scrollHeight > this.videoRef.current.wrapper.clientHeight
      height = this.videoRef.current.wrapper.scrollHeight
    }

    this.setState({ height, isVideoOverflowed })
  }

  onCloseMessage = () => this.setState({ action: null })

  onDeletePost = () => {
    const options = [
      {
        content: 'Cancelar',
        onClick: () => {
          this.onCloseMessage()
          this.setState({
            deleting: false,
          })
        },
        primary: true,
      },
      {
        content: 'Eliminar',
        onClick: () => {
          this.props.postListDelete(this.props.type, this.props.post.id, this.props.wall)
          this.onCloseMessage()
        },
      },
    ]
    this.setState({
      action: (
        <Message message="¿Realmente quieres eliminar este post?" onClose={this.onCloseMessage} options={options} />
      ),
      deleting: true,
    })
  }

  onExpandTextClick = (event) => {
    const {
      post: { post: content },
    } = this.props
    const post = content || this.props.post
    event.preventDefault()
    event.stopPropagation()

    this.setState({ expandText: true })
    this.forceUpdate()
    trackEvent('Component', 'Expand', `Post - ${post.id}`)
  }

  onImageModalClose = () => {
    document.body.classList.remove('noScroll')
    this.setState({ modal: false })
  }

  onImageModalOpen = () => {
    document.body.classList.add('noScroll')
    this.setState({ modal: true })
  }

  shouldRenderImage = () => !!this.props.post?.picture || !!this.props.post?.post?.picture

  shouldRenderURL = () => !this.shouldRenderImage() && (!!this.props.post?.url || !!this.props.post?.post?.url)

  static renderGeneralDate(date) {
    const result = moment.unix(date)
    return result.format('YYYY-MM-DD H:m')
  }

  static renderHumanDate(date) {
    const result = moment.unix(date)
    if (result.diff(moment(), 'days') < 3) {
      return result.fromNow()
    }
    if (result.diff(moment(), 'days') < 8) {
      return result.format('dddd, ha')
    }
    return result.format('DD [de] MMMM, ha')
  }

  renderImage = () => {
    const {
      post: { post: content, post_type: type },
      tinyDevice,
    } = this.props
    const post = content || this.props.post
    const { modal, width } = this.state

    const picture = (() => {
      if (post.picture) {
        let size = '320x400'
        if (width > 650) {
          size = '800x1000'
        } else if (width > 400) {
          size = '600x750'
        }
        return `${APP.services}/images/${post.picture.processed_at ? `${size}-post` : 'sbo'}/${post.picture.filename}`
      }
      return null
    })()

    const pictureModal = (() => {
      if (post.picture) {
        let size = '600x750'
        if (!tinyDevice) {
          size = '800x1000'
        }
        return `${APP.services}/images/${post.picture.processed_at ? `${size}-post` : 'sbo'}/${post.picture.filename}`
      }
      return null
    })()

    return (
      <>
        {picture && (
          <div className="border rounded-lg overflow-hidden p-0.5 bg-greyLightest">
            <Image
              centered
              className="rounded-md overflow-hidden"
              src={picture}
              target="_blank"
              verticalAlign="middle"
              wrapped
            />
          </div>
        )}
        {modal && (
          <Lightbox
            imageCaption={<PostReactions post={post} type={type} />}
            mainSrc={pictureModal}
            mainSrcThumbnail={picture}
            onCloseRequest={this.onImageModalClose}
            toolbarButtons={[<Icon key="close" color="green" name="close" onClick={this.onImageModalClose} />]}
            wrapperClassName="Post__content__image__modal"
          />
        )}
      </>
    )
  }

  renderURL = () => {
    const {
      post: { post: content },
    } = this.props
    const post = content || this.props.post
    const { height } = this.state

    if (post.url) {
      if (
        post.url.match(/(https?:\/\/\S*\.?youtube\.com\/\S+)/g) ||
        post.url.match(/(https?:\/\/\S*\.?youtu\.be\/\S+)/g) ||
        post.url.match(/(https?:\/\/\S*\.?dailymotion\.com\/\S+)/g) ||
        post.url.match(/(https?:\/\/\S*\.?twitch\.tv\/\S+)/g) ||
        post.url.match(/(https?:\/\/\S*\.?vimeo\.com\/\S+)/g) ||
        post.url.match(/(https?:\/\/\S*\.?giphy\.com\/\S+\.mp4)/g) ||
        post.url.match(/(https?:\/\/\S*\.?facebook\.com\/\S+)/g)
      ) {
        return (
          <ReactPlayer
            key={post.url}
            controls
            height={height}
            onReady={this.checkVideoOverflow}
            pip
            playsinline
            ref={this.videoRef}
            url={post.url.replace(/www\./i, '')}
            width="100%"
          />
        )
      }
      if (post.url.match(/(https?:\/\/\S*\.?soundcloud.com\/\S+)/g)) {
        return (
          <ReactPlayer
            key={post.url}
            controls
            height={150}
            pip
            playsinline
            url={post.url.replace(/www\./i, '')}
            width="100%"
          />
        )
      }
    }
    return null
  }

  render() {
    const {
      date,
      isAdmin,
      post: { post: content, post_type: type, sharer },
      smallDevice,
      viewer,
    } = this.props
    const { action, deleting, expandText } = this.state
    const post = content || this.props.post
    const { source, user } = post

    /**
     * @todo Remove this but test when it happens
     */
    if (!user || !user.id) {
      return null
    }

    const path = type.replaceAll(/([_]+)/g, '/')
    const link = (() => {
      if (type === 'sports_soccer_match' || type === 'user') {
        return `/user/${user.id}`
      }
      if (source && source.id) {
        return `/${path}/${source.id || user.id}`
      }
      return null
    })()

    const target = (() => {
      if (type === 'sports_soccer_match' && post.target) {
        const match = post.target
        const { local, visitor } = match

        const localAvatar = local.avatar
          ? `${APP.services}/images/45x45-fit/${local.avatar.filename}`
          : `${APP.url}/img/shield.png`

        const visitorAvatar = visitor.avatar
          ? `${APP.services}/images/45x45-fit/${visitor.avatar.filename}`
          : `${APP.url}/img/shield.png`

        return (
          <Link className="PostMatch" to={`/sports/soccer/match/${match.id}`}>
            <table>
              <tbody>
                <tr>
                  <td className="PostMatch__score-local">{smallDevice ? local.nickname : local.name}</td>
                  <td className="PostMatch__score-local-avatar">
                    <Image className="PostMatch__avatar avatar" src={localAvatar} />
                  </td>
                  <td className="PostMatch__score-score match">vs</td>
                  <td className="PostMatch__score-visitor-avatar">
                    <Image className="PostMatch__avatar avatar" src={visitorAvatar} />
                  </td>
                  <td className="PostMatch__score-visitor">{smallDevice ? visitor.nickname : visitor.name}</td>
                </tr>
              </tbody>
            </table>
          </Link>
        )
      }
      return null
    })()

    let IconImage = Avatar
    if (type === 'sports_soccer_match') {
      IconImage = Shield
    }
    if (type === 'institution') {
      IconImage = Build
    }

    return (
      <div className={classNames('Post bg-white border border-greyLight rounded-lg p-1', { deleting })}>
        {action}
        {!deleting && !!viewer && !this.props.post.willRemove && (
          <div className="Post__options">
            <PostOptions
              isAdmin={isAdmin}
              onBlockPost={Function.prototype}
              onDeletePost={this.onDeletePost}
              onEditPost={Function.prototype}
              onHidePost={this.onDeletePost}
              onUnsharePost={Function.prototype}
              post={this.props.post}
              viewer={viewer}
            />
          </div>
        )}
        <div className="Post__header">
          <div className="Post__header__avatar">
            {link ? (
              <Link to={link}>
                <IconImage size="2.8125em" src={source && source.avatar} />
              </Link>
            ) : (
              <IconImage size="2.8125em" src={source && source.avatar} />
            )}
          </div>
          <div className="Post__header__title">
            {link ? (
              <Link className="Post__user" to={link}>
                {get(source, 'name') || user.name}
              </Link>
            ) : (
              get(source, 'name') || user.name
            )}
            <em className="Post__date" title={Post.renderGeneralDate(date)}>
              {Post.renderHumanDate(date)}
            </em>
            {!!sharer && (
              <div className="Post__sharer">
                <span>Compartido por:</span>{' '}
                <Link to={`/user/${sharer.id}`}>
                  <b>{sharer.name}</b>
                </Link>
              </div>
            )}
            {!sharer && isAdmin && (
              <div className="Post__sharer">
                <span>Creado por:</span>{' '}
                <Link to={`/user/${user.id}`}>
                  <b>{user.name}</b>
                </Link>
              </div>
            )}
          </div>
          {!!target && <div className="Post__header__target">{target}</div>}
        </div>
        <div className="Post__content">
          <div
            className={classNames('pb-2', {
              el: !!this.state.messageEl,
              expanded: expandText,
            })}
          >
            <div
              onClick={expandText ? null : this.onExpandTextClick}
              onKeyDown={expandText ? null : this.onExpandTextClick}
              ref={(messageEl) => {
                this.setState({ messageEl })
                return messageEl
              }}
              role="button"
              tabIndex={-1}
            >
              <Markdown>{post.message}</Markdown>
            </div>
            {!expandText && (
              <button className="more" onClick={this.onExpandTextClick} type="button">
                <span>...ver más.</span>
              </button>
            )}
          </div>
          {this.shouldRenderImage() && this.renderImage()}
          {this.shouldRenderURL() && this.renderURL()}
          {!deleting && <PostReactions post={post} type={type} />}
        </div>
      </div>
    )
  }
}

Post.propTypes = {
  date: PropTypes.number.isRequired,
  post: PropTypes.oneOfType([SharedPostSchema, PostSchema]).isRequired,
  type: PropTypes.oneOf([
    'advertising',
    'institution',
    'sports_soccer_match',
    'sports_soccer_team',
    'sports_tournament',
    'stats',
    'user',
  ]).isRequired,

  isAdmin: PropTypes.bool,
  postListDelete: PropTypes.func,
  smallDevice: PropTypes.bool,
  tinyDevice: PropTypes.bool,
  viewer: UserSchema,
  wall: PropTypes.bool,
}

Post.defaultProps = {
  isAdmin: false,
  postListDelete: Function.prototype,
  smallDevice: false,
  tinyDevice: false,
  viewer: null,
  wall: false,
}

export class Pure extends Post {}

export default connect(
  ({ auth: { usr }, environment: { stage } }) => ({
    smallDevice: stage.small,
    tinyDevice: stage.tiny,
    viewer: usr,
  }),
  { postListDelete },
)(Post)
